'use client'

import { useCallback, useState } from 'react'
import { EDIT_TABS, type EditReservationTab } from '@/root/modules/shared/EditReservation/consts'
import type { TripDirection } from '@/root/modules/shared/EditReservation/types'

type PendingNavigation =
  | { tab: EditReservationTab; type: 'tab' }
  | { direction: TripDirection; type: 'direction' }
  | null

interface UseEditNavigationParams {
  clearError?: () => void
  hasCustomerChanges: boolean
  /**
   * Unsaved work on the tickets tab, per leg and combined: passenger forms without guest data and, where a
   * client has vehicles, open vehicle and trailer forms. The tickets tab reports them as one value per leg.
   */
  hasUnsavedOutboundForms: boolean
  hasUnsavedPassengerForms: boolean
  hasUnsavedReturnForms: boolean
  /** A save or a ticket operation is in flight: tab and direction requests are ignored. */
  isModifying: boolean
  resetCustomerForm: () => void
}

/** Tab and direction of the edit page, and the unsaved-changes dialog that guards leaving them. */
export const useEditNavigation = ({
  clearError,
  hasCustomerChanges,
  hasUnsavedOutboundForms,
  hasUnsavedPassengerForms,
  hasUnsavedReturnForms,
  isModifying,
  resetCustomerForm,
}: UseEditNavigationParams) => {
  const [activeTab, setActiveTab] = useState<EditReservationTab>(EDIT_TABS.TICKETS)
  const [activeDirection, setActiveDirection] = useState<TripDirection>('outbound')
  const [pendingNavigation, setPendingNavigation] = useState<PendingNavigation>(null)

  const currentTabHasChanges =
    activeTab === EDIT_TABS.CUSTOMER
      ? hasCustomerChanges
      : activeTab === EDIT_TABS.TICKETS
        ? hasUnsavedPassengerForms
        : false

  const requestTab = useCallback(
    (tab: EditReservationTab) => {
      if (tab === activeTab) return
      if (isModifying) return
      if (currentTabHasChanges) {
        setPendingNavigation({ type: 'tab', tab })
        return
      }
      clearError?.()
      setActiveTab(tab)
    },
    [activeTab, isModifying, currentTabHasChanges, clearError]
  )

  const currentDirectionHasChanges = activeDirection === 'outbound' ? hasUnsavedOutboundForms : hasUnsavedReturnForms

  const requestDirection = useCallback(
    (direction: TripDirection) => {
      if (direction === activeDirection || isModifying) return
      if (currentDirectionHasChanges) {
        setPendingNavigation({ type: 'direction', direction })
        return
      }
      clearError?.()
      setActiveDirection(direction)
    },
    [activeDirection, isModifying, currentDirectionHasChanges, clearError]
  )

  // The unsaved-form flags stay as they are: they mark a ticket without guest data, and the tickets tab
  // recomputes them on remount; resetting them here unlocked Finish with an unfilled passenger.
  const discardAndNavigate = useCallback(() => {
    if (pendingNavigation?.type === 'direction') {
      clearError?.()
      setActiveDirection(pendingNavigation.direction)
      setPendingNavigation(null)
      return
    }
    if (pendingNavigation?.type !== 'tab') return
    if (activeTab === EDIT_TABS.CUSTOMER) {
      resetCustomerForm()
    }
    clearError?.()
    setActiveTab(pendingNavigation.tab)
    setPendingNavigation(null)
  }, [pendingNavigation, activeTab, resetCustomerForm, clearError])

  const closeUnsavedModal = useCallback(() => {
    setPendingNavigation(null)
  }, [])

  return {
    activeTab,
    activeDirection,
    pendingNavigation,
    requestTab,
    requestDirection,
    discardAndNavigate,
    closeUnsavedModal,
  }
}
